import {
  useApiClient,
  useEntityMetadataIncludeExternal,
  useHistoryContext,
  useModuleFederationContext,
  useUrlContext,
  useViewBaseMetadata,
  useViewMetadata,
} from '@msdk/common'
import {
  CompositeConditionDefinition,
  Entity,
  EntityModel,
  GenericFieldValue,
  OperatorType,
  PagedList,
  SimpleConditionDefinition,
  VIEW_ACL_DEFAULT,
  ViewAcl,
  ViewModel,
} from '@msdk/definitions'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import { $countCollection } from '../state'

const DEFAULT_PAGE_SIZE = 30

function useViewBase(
  entityName: string,
  viewName: string,
  multiselect?: boolean,
  filters?: Record<string, GenericFieldValue>,
  containerEntity?: Entity,
  pageSize?: number,
  preselectedRows?: string[],
) {
  const api = useApiClient()
  const { loadedModules } = useModuleFederationContext()
  const entityModel = useEntityMetadataIncludeExternal(entityName, viewName) as EntityModel
  const viewModel = useViewMetadata(entityName, viewName) as ViewModel
  const baseMetadata = useViewBaseMetadata(entityName, viewName)
  const { context } = useUrlContext()
  const [_, setCountCollection] = useRecoilState($countCollection)

  const multiselectInitialValue = multiselect ?? viewModel.multiSelection
  const additionalConditionInitialValue = {
    logicalOperator: 'AND',
    conditions: [],
    not: false,
  }

  const [isLoading, setIsLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [response, setResponse] = useState<PagedList | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [currentFilters, setCurrentFilters] = useState<Record<string, GenericFieldValue>>(filters ?? {})
  const [initialFilters, setInitialFilter] = useState<Record<string, GenericFieldValue>>({})
  const [currentOrder, setCurrentOrder] = useState<string>('')
  const [selectedRows, setSelectedRows] = useState<Entity[]>([])
  const [isMultiselected, setIsMultiselect] = useState<boolean>(multiselectInitialValue)
  const [acl, setAcl] = useState<ViewAcl>(VIEW_ACL_DEFAULT)
  const [additionalCondition, setAdditionalConditions] = useState<CompositeConditionDefinition>(
    additionalConditionInitialValue,
  )

  /**
   * Retrieve context entity info, usually the context of the view is the parent of the entity
   * for example  a LAB_DATA view has an entity context of PATIENT because it is the parent,
   * and this information is found inside the context of the current url route.
   * In case of and embedded view the context is the entity where the embedded view is contained
   */
  const getContextInfo = () => {
    let contextEntity = entityModel.parent
    let contextId = context[contextEntity]

    if (!contextId && containerEntity) {
      contextId = containerEntity.id
    }

    if (!contextEntity && containerEntity) {
      contextEntity = containerEntity.entityName
      contextId = containerEntity.id
    }

    return { contextEntity, contextId }
  }

  const updateCount = (count: number) => {
    setCountCollection((curr) => {
      const newCollection = curr.clone()
      newCollection.upsertItem(entityName, viewName, count)
      return newCollection
    })
  }

  const initialInit = (
    existingFilters?: Record<string, GenericFieldValue>,
    initialColumnFilters?: CompositeConditionDefinition,
  ) => {
    let initialFiltersValue = filters ?? {}
    initialFiltersValue = existingFilters ? { ...initialFiltersValue, ...existingFilters } : initialFiltersValue

    let initialConditions: CompositeConditionDefinition | undefined =
      additionalConditionInitialValue.conditions.length > 0 ? additionalConditionInitialValue : undefined
    initialConditions = initialColumnFilters ? initialColumnFilters : initialConditions
    //force the multiselect to be true if the filter contains the INACTIVE status, otherwise use the initial value (either from param or from viewModel)
    const shouldBeMultiselect =
      initialFiltersValue['@ENTITYSTATUS']?.value === 'INACTIVE' ? true : multiselectInitialValue

    const fetch = async () => {
      setIsLoading(true)
      setIsInitialized(false)
      const contextInfo = getContextInfo()
      const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
        contextName: contextInfo.contextEntity,
        contextId: contextInfo.contextId,
        filters: initialFiltersValue,
        parentId: contextInfo.contextId,
        pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
        extraConditions: initialConditions,
      })

      if (preselectedRows?.length) {
        const existingRows = res.source.filter((r) => preselectedRows.includes(r.id))
        const missingIds = preselectedRows.filter((id) => !res.source.some((r) => r.id === id))

        /**
         * We need to fetch the selected rows across all pages, not just the first one.
         * Otherwise, if you save the form (List -> Lookup) before navigating through them,
         * some preselected rows may be missed.
         */
        if (missingIds.length) {
          // Fetchs all the missing rows
          const { entities: missingRows } = await api.entity.getEntities(
            baseMetadata?.msProvider as string,
            missingIds,
            entityModel,
          )
          existingRows.push(...missingRows)
        }
        onRowSelected(existingRows)
      }
      setResponse(res)
      setAcl(res.metadata)
      setCurrentFilters(initialFiltersValue)
      setInitialFilter(initialFiltersValue)
      setCurrentOrder('')
      setIsMultiselect(shouldBeMultiselect)
      setAdditionalConditions((initialConditions as CompositeConditionDefinition) ?? additionalConditionInitialValue)
      updateCount(res.totalItems)
      setIsLoading(false)
    }
    if (entityModel && viewModel && baseMetadata && loadedModules[baseMetadata.msProvider]) {
      fetch().catch(console.error)
    }
  }

  useEffect(() => {
    if (!isLoading) {
      setIsInitialized(true)
    }
  }, [isLoading])

  const addOrUpdateFilter = async (fieldName: string, operator: OperatorType, value: GenericFieldValue) => {
    const condition: SimpleConditionDefinition = {
      id: crypto.randomUUID(),
      field: fieldName,
      not: false,
      operator,
      value: value?.value as string,
      fieldValue: value,
    }

    const existingConditionByFieldAndOperator = additionalCondition.conditions.find(
      (c) =>
        (c as SimpleConditionDefinition)?.field === condition.field &&
        (c as SimpleConditionDefinition)?.operator === condition.operator,
    ) as SimpleConditionDefinition

    if (existingConditionByFieldAndOperator) {
      condition.id = existingConditionByFieldAndOperator.id
    }

    await addColumnFilter(condition)
  }

  const addColumnFilter = async (condition: SimpleConditionDefinition) => {
    const cloned = { ...additionalCondition }
    const c = cloned.conditions.find(
      // eslint-disable-next-line @typescript-eslint/no-shadow
      (c) => (c as SimpleConditionDefinition).id === condition.id,
    ) as SimpleConditionDefinition

    if (c) {
      c.field = condition.field
      c.operator = condition.operator
      c.value = condition.value
      c.fieldValue = condition.fieldValue
    } else {
      cloned.conditions.push(condition)
    }

    setAdditionalConditions(cloned)

    setIsLoading(true)
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      orderOverride: currentOrder !== '' ? currentOrder : undefined,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: cloned.conditions.length > 0 ? cloned : undefined,
    })
    setResponse(res)
    setCurrentPage(0)
    updateCount(res.totalItems)
    setIsLoading(false)

    return cloned
  }

  const removeColumnFilter = async (id: string) => {
    const cloned = { ...additionalCondition }
    const index = cloned.conditions.findIndex((c) => (c as SimpleConditionDefinition).id === id)
    if (index === -1) {
      return
    }

    cloned.conditions.splice(index, 1)
    setAdditionalConditions(cloned)

    setIsLoading(true)
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      orderOverride: currentOrder !== '' ? currentOrder : undefined,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: cloned.conditions.length > 0 ? cloned : undefined,
    })
    setResponse(res)
    setCurrentPage(0)
    updateCount(res.totalItems)
    setIsLoading(false)

    return cloned
  }

  const removeAllColumnFilter = async (orderOverride?: string) => {
    const cloned = { ...additionalCondition }
    cloned.conditions = []
    setAdditionalConditions(cloned)

    setIsLoading(true)
    const contextInfo = getContextInfo()

    orderOverride = (orderOverride ?? currentOrder) || undefined

    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      orderOverride,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: cloned.conditions.length > 0 ? cloned : undefined,
    })
    setResponse(res)

    if (orderOverride) {
      setCurrentOrder(orderOverride)
    }

    setCurrentPage(0)
    updateCount(res.totalItems)

    setIsLoading(false)
  }

  const [initialMultiselect] = useState(isMultiselected)

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const filterChange = async (filters: Record<string, GenericFieldValue>) => {
    setIsLoading(true)
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters,
      orderOverride: currentOrder !== '' ? currentOrder : undefined,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: additionalCondition.conditions.length > 0 ? additionalCondition : undefined,
    })
    setResponse(res)
    setCurrentFilters(filters)
    setCurrentPage(0)
    updateCount(res.totalItems)

    const statusFilter = filters['@ENTITYSTATUS']
    const statusValue = statusFilter?.value
    if (!statusValue || statusValue === 'ACTIVE') {
      setIsMultiselect(initialMultiselect)
    } else if (statusValue === 'INACTIVE') {
      setIsMultiselect(true)
    }
    setIsLoading(false)
  }

  /**
   * Clears all inline and column filters, then resets initialFilters so he Filters component's local UI state re-syncs to empty.
   */
  const clearAllFilters = async () => {
    setIsLoading(true)
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      orderOverride: currentOrder || undefined,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: undefined,
    })
    setResponse(res)
    setCurrentFilters({})
    setInitialFilter({})
    setAdditionalConditions(additionalConditionInitialValue)
    setCurrentPage(0)
    updateCount(res.totalItems)
    setIsMultiselect(initialMultiselect)
    setIsLoading(false)
  }

  const onPageChange = async (page: number) => {
    setIsLoading(true)
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      pageNumber: page,
      orderOverride: currentOrder !== '' ? currentOrder : undefined,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: additionalCondition.conditions.length > 0 ? additionalCondition : undefined,
    })

    setResponse(res)
    setCurrentPage(page)
    updateCount(res.totalItems)
    setIsLoading(false)
  }

  const reload = useCallback(async () => {
    setIsLoading(true)
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      pageNumber: currentPage,
      orderOverride: currentOrder !== '' ? currentOrder : undefined,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: additionalCondition.conditions.length > 0 ? additionalCondition : undefined,
    })

    // check if the currentPage is not available anymore
    // i.e. if an element was deleted from the last page
    // go back to the last possible page
    if (currentPage >= res.totalPages && res.totalPages > 0) {
      setIsLoading(false)
      const newPage = res.totalPages - 1
      onPageChange(newPage)
      return
    }

    setSelectedRows([])
    setResponse(res)
    updateCount(res.totalItems)
    setIsLoading(false)
  }, [currentFilters, currentPage, currentOrder, additionalCondition])

  const onOrderChange = async (orderBy: string, order: 'asc' | 'desc') => {
    setIsLoading(true)
    const orderString = `${orderBy}:${order}`
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      pageNumber: currentPage,
      orderOverride: orderString,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: additionalCondition.conditions.length > 0 ? additionalCondition : undefined,
    })
    setCurrentOrder(orderString)
    setResponse(res)
    updateCount(res.totalItems)
    setIsLoading(false)
  }

  function onRowSelected(rows: Entity[]) {
    setSelectedRows(rows)
  }

  function setMultiselect(value: boolean) {
    setIsMultiselect(value)
    if (!value) {
      setSelectedRows([])
    }
  }

  return {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    clearAllFilters,
    onPageChange,
    onOrderChange,
    reload,
    selectedRows,
    currentOrder,
    currentFilters,
    initialFilters,
    onRowSelected,
    setResponse,
    setIsLoading,
    isMultiselected,
    setIsMultiselect: setMultiselect,
    getContextInfo,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    isInitialized,
    initialInit,
    baseMetadata,
    loadedModules,
    addOrUpdateFilter,
  }
}

export function useView(entityName: string, viewName: string) {
  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    clearAllFilters,
    onPageChange,
    onOrderChange,
    reload,
    selectedRows,
    currentOrder,
    currentFilters,
    initialFilters,
    onRowSelected,
    isMultiselected,
    setIsMultiselect,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    isInitialized,
    initialInit,
    baseMetadata,
    loadedModules,
    addOrUpdateFilter,
  } = useViewBase(entityName, viewName)

  const context = useUrlContext()
  const history = useHistoryContext()
  const location = useLocation()

  const parseFiltersFromContext = () => {
    const filterString = context.context['f']
    if (filterString) {
      const result: Record<string, GenericFieldValue> = {}
      const filterObject = JSON.parse(filterString)
      viewModel.filters.forEach((f) => {
        if (filterObject[f.parameterKey]) {
          const filterValue = filterObject[f.parameterKey]
          const value = f.inlineDefinition.getFieldValue()
          value.mapField(filterValue, f.inlineDefinition)
          result[f.parameterKey] = value
        }
      })
      return result
    }
    return undefined
  }

  const { pathname } = location
  useEffect(() => {
    const currentPathName = pathname
    const lastVisitedViewPathName = history.historyStack.getLastVisitedView()?.pathname //todo cri non posso mettere history come dipendenza e non va bene

    const contextFromUrl = context.deserializeUrl(window.location.search)
    const columnFilterString = contextFromUrl['cf']
    const existingFilters: Record<string, GenericFieldValue> | undefined =
      currentPathName === lastVisitedViewPathName ? parseFiltersFromContext() : undefined
    const initialColumnFilters: CompositeConditionDefinition | undefined = columnFilterString
      ? JSON.parse(columnFilterString)
      : undefined

    initialInit(existingFilters, initialColumnFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, viewName, viewModel, entityModel, baseMetadata, loadedModules, pathname])

  const updateUrl = (filters: Record<string, GenericFieldValue>, conditions?: CompositeConditionDefinition) => {
    const current = { ...context.context }

    const tabValue = context.deserializeUrl(window.location.search)['t']
    if (tabValue !== null && tabValue !== undefined) {
      current['t'] = tabValue
    }
    current['cf'] = JSON.stringify(conditions)
    current['f'] = JSON.stringify(filters)

    if (!conditions || !conditions.conditions || conditions.conditions.length === 0) {
      delete current['cf']
    }

    if (!filters || Object.keys(filters).length === 0) {
      delete current['f']
    }

    const serialized = context.serialize(current)
    const url = new URL(window.location.href)
    url.searchParams.set('s', serialized)
    history.updateCurrentSearch(url.searchParams.toString())
    window.history.replaceState({}, '', url.toString())
  }

  const pageViewFilterChange = async (filters: Record<string, GenericFieldValue>) => {
    await filterChange(filters)
    updateUrl(filters, additionalCondition)
  }

  const pageViewClearAllFilters = async () => {
    await clearAllFilters()
    updateUrl({}, undefined)
  }

  const pageViewAddColumnFilter = async (condition: SimpleConditionDefinition) => {
    const conditions = await addColumnFilter(condition)
    updateUrl(currentFilters, conditions)
  }

  const pageViewRemoveColumnFilter = async (id: string) => {
    const conditions = await removeColumnFilter(id)
    updateUrl(currentFilters, conditions)
  }

  const pageViewRemoveAllColumnFilter = async (orderOverride?: string) => {
    await removeAllColumnFilter(orderOverride)
    updateUrl(currentFilters, undefined)
  }

  return {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange: pageViewFilterChange,
    clearAllFilters: pageViewClearAllFilters,
    onPageChange,
    onOrderChange,
    reload,
    selectedRows,
    onRowSelected,
    currentOrder,
    currentFilters,
    initialFilters,
    isMultiselected,
    setIsMultiselect,
    addColumnFilter: pageViewAddColumnFilter,
    removeColumnFilter: pageViewRemoveColumnFilter,
    removeAllColumnFilter: pageViewRemoveAllColumnFilter,
    additionalCondition,
    isInitialized,
    addOrUpdateFilter,
  }
}

export function useModalView(
  entityName: string,
  viewName: string,
  multiselect: boolean,
  preselectedRows?: string[],
  filters?: Record<string, GenericFieldValue>,
) {
  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    onPageChange,
    onOrderChange,
    reload,
    selectedRows,
    currentOrder,
    currentFilters,
    initialFilters,
    onRowSelected,
    setResponse,
    setIsLoading,
    isMultiselected,
    setIsMultiselect,
    getContextInfo,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    isInitialized,
    initialInit,
    loadedModules,
    addOrUpdateFilter,
  } = useViewBase(entityName, viewName, multiselect, filters, undefined, undefined, preselectedRows)

  const api = useApiClient()
  const baseMetadata = useViewBaseMetadata(entityName, viewName)

  const fetchData = useCallback(async () => {
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      extraConditions: additionalCondition.conditions.length > 0 ? additionalCondition : undefined,
    })
    setResponse(res)
    setIsLoading(false)
    if (preselectedRows) {
      const toSet = res.source.filter((r) => preselectedRows.includes(r.id))
      onRowSelected(toSet)
    }
  }, [])

  useEffect(() => {
    initialInit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, viewName, viewModel, entityModel, baseMetadata, loadedModules])

  return {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    onPageChange,
    onOrderChange,
    reload,
    selectedRows,
    onRowSelected,
    currentOrder,
    currentFilters,
    initialFilters,
    fetchData,
    isMultiselected,
    setIsMultiselect,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    isInitialized,
    addOrUpdateFilter,
  }
}

export function useEmbeddedView(
  entityName: string,
  viewName: string,
  containerEntity: Entity,
  filters?: Record<string, GenericFieldValue>,
  pageSize?: number,
) {
  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    clearAllFilters,
    onPageChange,
    onOrderChange,
    reload,
    selectedRows,
    currentOrder,
    currentFilters,
    initialFilters,
    onRowSelected,
    setResponse,
    setIsLoading,
    isMultiselected,
    setIsMultiselect,
    getContextInfo,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    isInitialized,
    initialInit,
    addOrUpdateFilter,
    loadedModules,
  } = useViewBase(entityName, viewName, false, filters, containerEntity, pageSize)

  const api = useApiClient()
  const baseMetadata = useViewBaseMetadata(entityName, viewName)

  useEffect(() => {
    initialInit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, viewName, viewModel, entityModel, baseMetadata, loadedModules])

  const fetchData = async () => {
    const contextInfo = getContextInfo()
    const res = await api.entity.getView(baseMetadata?.msProvider as string, entityModel, viewName, {
      filters: currentFilters,
      pageNumber: currentPage,
      contextName: contextInfo.contextEntity,
      contextId: contextInfo.contextId,
      parentId: contextInfo.contextId,
      pageSize: pageSize ?? viewModel.pageSize ?? DEFAULT_PAGE_SIZE,
      extraConditions: additionalCondition.conditions.length > 0 ? additionalCondition : undefined,
    })
    setResponse(res)
    setIsLoading(false)
  }

  return {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    clearAllFilters,
    onPageChange,
    onOrderChange,
    reload,
    selectedRows,
    onRowSelected,
    currentOrder,
    currentFilters,
    initialFilters,
    fetchData,
    isMultiselected,
    setIsMultiselect,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    isInitialized,
    addOrUpdateFilter,
  }
}
